<template>
  <div class="score-rank-page ig-score-rank-page">
    <!-- 页面标题及主要操作 -->
    <div class="page-header">
      <div>
        <div class="page-title">
          <span class="title-mark"></span>
          积分排行榜
        </div>
        <div class="page-subtitle">查看员工积分汇总及点赞卡明细</div>
      </div>
      <el-button
        type="success"
        icon="el-icon-download"
        @click="exportData"
      >
        导出已选
        <span v-if="selectedRows.length">（{{ selectedRows.length }}）</span>
      </el-button>
    </div>

    <!-- 筛选区 -->
    <div class="filter-panel">
      <div class="filter-main">
        <div class="filter-label">
          <i class="el-icon-date"></i>
          统计周期
        </div>
        <el-date-picker
          v-model="dateRange"
          class="date-range-picker"
          type="daterange"
          :picker-options="pickerOptions"
          range-separator="至"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          align="right"
          size="medium"
          value-format="yyyy-MM-dd HH:mm:ss"
        />
        <el-button type="success" icon="el-icon-search" @click="handleQuery">
          查询
        </el-button>
        <el-button icon="el-icon-refresh-left" @click="resetQuery">
          重置
        </el-button>
      </div>
      <div class="filter-tip">支持今日、本周、本月等快捷时间范围</div>
    </div>

    <!-- 员工积分明细 -->
    <div class="table-panel">
      <div class="table-toolbar">
        <div class="table-title">
          员工积分明细
          <span class="total-tag">共 {{ total }} 人</span>
        </div>
        <div v-if="selectedRows.length" class="selected-tip">
          已选择 {{ selectedRows.length }} 人
        </div>
      </div>

      <el-table
        ref="table"
        v-loading="tableLoading"
        class="rank-table"
        :data="tableData"
        border
        stripe
        empty-text="暂无积分数据"
        style="width: 100%"
        @selection-change="handleSelectionChange"
      >
        <el-table-column type="selection" width="50" align="center" />
        <el-table-column
          prop="empNo"
          label="工号"
          align="center"
          min-width="95"
          show-overflow-tooltip
        />
        <el-table-column
          prop="empName"
          label="姓名"
          align="center"
          min-width="95"
          show-overflow-tooltip
        />
        <el-table-column
          prop="phone"
          label="手机号"
          align="center"
          min-width="125"
          show-overflow-tooltip
        />
        <el-table-column
          prop="boardDate"
          label="入职时间"
          align="center"
          min-width="110"
        >
          <template slot-scope="{ row }">
            <span>{{
              row.boardDate ? moment(row.boardDate).format("YYYY-MM-DD") : "-"
            }}</span>
          </template>
        </el-table-column>

        <el-table-column
          prop="gender"
          label="性别"
          align="center"
          width="75"
        >
          <template slot-scope="{ row }">
            <span>{{
              row.gender == 760 ? "男" : row.gender == 761 ? "女" : "-"
            }}</span>
          </template>
        </el-table-column>
        <el-table-column
          prop="orgName"
          label="所属部门"
          align="center"
          min-width="180"
          show-overflow-tooltip
        />
        <el-table-column
          prop="pointsSum"
          label="新增积分"
          align="center"
          sortable
          width="115"
        >
          <template slot-scope="{ row }">
            <span class="points-value added">{{
              formatPoints(row.pointsSum)
            }}</span>
          </template>
        </el-table-column>
        <el-table-column
          prop="totalPoints"
          label="总积分"
          align="center"
          sortable
          width="110"
        >
          <template slot-scope="{ row }">
            <span class="points-value total">{{
              formatPoints(row.totalPoints)
            }}</span>
          </template>
        </el-table-column>
        <el-table-column
          prop="availablePoints"
          label="未同步积分"
          align="center"
          sortable
          width="120"
        >
          <template slot-scope="{ row }">
            <span class="points-value pending">{{
              formatPoints(row.availablePoints)
            }}</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" align="center" width="125" fixed="right">
          <template slot-scope="{ row }">
            <el-button
              type="success"
              plain
              size="mini"
              icon="el-icon-document"
              @click="openScoreRecordDialog(row)"
            >
              查看记录
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <el-pagination
        class="pagination"
        background
        layout="total, sizes, prev, pager, next, jumper"
        :total="total"
        :current-page="pageData.page"
        :page-size="pageData.pageSize"
        :page-sizes="[10, 20, 50, 100]"
        @current-change="handlePageChange"
        @size-change="handlePageSizeChange"
      />
    </div>

    <!-- 积分记录弹窗 -->
    <el-dialog
      :title="`点赞卡记录${currentUserInfo.empName ? ` · ${currentUserInfo.empName}` : ''}`"
      :visible.sync="scoreRecordDialogVisible"
      width="88%"
      top="5vh"
      append-to-body
      :close-on-click-modal="false"
      custom-class="ig-score-record-dialog"
    >
      <!-- 顶部用户及积分概览 -->
      <div class="record-header">
        <div class="user-profile">
          <div class="user-avatar">
            <i class="el-icon-user-solid"></i>
          </div>
          <div class="user-content">
            <div class="employee-name">
              {{ currentUserInfo.empName || "-" }}
            </div>
            <div class="user-meta">
              <div class="meta-item">
                <span class="meta-label">工号</span>
                <span class="meta-value">{{
                  currentUserInfo.empNo || "-"
                }}</span>
              </div>
              <span class="meta-divider"></span>
              <div class="meta-item department-item">
                <span class="meta-label">部门</span>
                <span class="meta-value">{{
                  currentUserInfo.orgName || "-"
                }}</span>
              </div>
            </div>
          </div>
        </div>
        <div class="score-info">
          <div class="score-card total-points">
            <div class="score-label">
              <i class="el-icon-medal"></i>
              总积分
            </div>
            <div class="score-value">
              {{ currentUserInfo.totalPoints || 0 }}
            </div>
          </div>
          <div class="score-card pending-points">
            <div class="score-label">
              <i class="el-icon-time"></i>
              未同步积分
            </div>
            <div class="score-value">
              {{ currentUserInfo.availablePoints || 0 }}
            </div>
          </div>
        </div>
      </div>

      <!-- 积分记录表格 -->
      <el-table
        v-loading="recordLoading"
        class="record-table"
        :data="scoreRecordList"
        border
        stripe
        max-height="600"
        empty-text="暂无点赞卡记录"
        style="width: 100%"
      >
        <el-table-column
          prop="create_time"
          label="时间"
          align="center"
          width="180"
        >
          <template slot-scope="{ row }">
            {{ formatRecordTime(row.create_time) }}
          </template>
        </el-table-column>
        <el-table-column
          prop="sendername"
          label="发卡人"
          align="center"
          width="120"
          show-overflow-tooltip
        />
        <el-table-column
          prop="deptName"
          label="发卡人部门"
          align="center"
          min-width="160"
          show-overflow-tooltip
        />
        <el-table-column
          prop="point"
          label="积分变动"
          align="center"
          width="120"
        >
          <template slot-scope="{ row }">
            <span
              class="record-points"
              :class="{ decrease: Number(row.point) < 0 }"
            >
              {{ formatChangedPoints(row.point) }}
            </span>
          </template>
        </el-table-column>
        <el-table-column
          prop="reason"
          label="点赞卡内容"
          align="left"
          min-width="280"
          show-overflow-tooltip
        />

        <el-table-column label="点赞" align="center" width="110">
          <template slot-scope="{ row }">
            <button
              type="button"
              class="like-button"
              :class="{ active: row.likeStatus == 1 }"
              @click="handleLike(row)"
            >
              <i class="el-icon-thumb"></i>
              {{ row.likeStatus == 1 ? "已点赞" : "点赞" }}
            </button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 弹窗分页 -->
      <el-pagination
        class="record-pagination"
        background
        layout="total, sizes, prev, pager, next, jumper"
        :total="scoreRecordTotal"
        :current-page="recordPageData.page"
        :page-size="recordPageData.pageSize"
        :page-sizes="[10, 20, 50]"
        @current-change="handleRecordPageChange"
        @size-change="handleRecordPageSizeChange"
      />
    </el-dialog>
  </div>
</template>

<script>
import moment from "moment";
import api from '@/apis/medal'

export default {
  name: 'ScoreRank',
  data() {
    return {
      moment,
      dateRange: [],
      pickerOptions: {
        shortcuts: [
          { text: '今日', onClick(picker) {
              const start = new Date()
              start.setHours(0,0,0,0)
              const end = new Date()
              end.setHours(23,59,59,999)
              picker.$emit('pick', [start, end])
            }
          },
          { text: '本周', onClick(picker) {
              const start = new Date()
              const day = start.getDay() || 7
              start.setTime(start.getTime() - (day - 1) * 86400000)
              start.setHours(0,0,0,0)
              const end = new Date()
              end.setHours(23,59,59,999)
              picker.$emit('pick', [start, end])
            }
          },
          { text: '本月', onClick(picker) {
              const start = new Date()
              start.setDate(1)
              start.setHours(0,0,0,0)
              const end = new Date()
              end.setHours(23,59,59,999)
              picker.$emit('pick', [start, end])
            }
          },
          { text: '本季度', onClick(picker) {
              const start = new Date()
              const quarter = Math.floor(start.getMonth() / 3)
              start.setMonth(quarter * 3, 1)
              start.setHours(0,0,0,0)
              const end = new Date()
              end.setHours(23,59,59,999)
              picker.$emit('pick', [start, end])
            }
          },
          { text: '本年度', onClick(picker) {
              const start = new Date()
              start.setMonth(0, 1)
              start.setHours(0,0,0,0)
              const end = new Date()
              end.setHours(23,59,59,999)
              picker.$emit('pick', [start, end])
            }
          },
          { text: '最近7天', onClick(picker) {
              const end = new Date()
              end.setHours(23,59,59,999)
              const start = new Date()
              start.setTime(start.getTime() - 3600 * 1000 * 24 * 7)
              start.setHours(0,0,0,0)
              picker.$emit('pick', [start, end])
            }
          },
          { text: '最近30天', onClick(picker) {
              const end = new Date()
              end.setHours(23,59,59,999)
              const start = new Date()
              start.setTime(start.getTime() - 3600 * 1000 * 24 * 30)
              start.setHours(0,0,0,0)
              picker.$emit('pick', [start, end])
            }
          },
          { text: '最近90天', onClick(picker) {
              const end = new Date()
              end.setHours(23,59,59,999)
              const start = new Date()
              start.setTime(start.getTime() - 3600 * 1000 * 24 * 90)
              start.setHours(0,0,0,0)
              picker.$emit('pick', [start, end])
            }
          }
        ]
      },
      tableLoading: false,
      recordLoading: false,
      selectedRows: [],
      pageData: { page: 1, pageSize: 10 },
      total: 0,
      tableData: [],

      scoreRecordDialogVisible: false,
      currentUserInfo: {},
      scoreRecordList: [],
      scoreRecordTotal: 0,
      recordPageData: { page: 1, pageSize: 10 }
    }
  },
  mounted() {
    this.queryData()
  },
  methods: {
    // 查询时回到第一页，避免筛选后停留在无数据页
    handleQuery() {
      this.pageData.page = 1
      this.queryData()
    },

    resetQuery() {
      this.dateRange = []
      this.pageData.page = 1
      this.queryData()
    },

    async queryData() {
      try {
        this.tableLoading = true
        const range = this.dateRange || []
        const params = {
          page: this.pageData.page,
          pageSize: this.pageData.pageSize,
          startTime: range[0] || null,
          endTime: range[1] || null
        }
        const res = await api.getPointRank(params)
        this.tableData = res.data?.list || []
        this.total = res.data?.total || 0
      } catch (error) {
        this.$message.error('查询失败')
      } finally {
        this.tableLoading = false
      }
    },

    handleSelectionChange(val) {
      this.selectedRows = val
    },

    exportData() {
      if (this.selectedRows.length === 0) {
        this.$message.warning('请先选择要导出的数据')
        return
      }
      this.$message.success('导出功能开发中...')
    },

    handlePageChange(page) {
      this.pageData.page = page
      this.queryData()
    },

    handlePageSizeChange(pageSize) {
      this.pageData.pageSize = pageSize
      this.pageData.page = 1
      this.queryData()
    },

    openScoreRecordDialog(row) {
      this.currentUserInfo = { ...row }
      this.recordPageData.page = 1
      this.scoreRecordList = []
      this.scoreRecordDialogVisible = true
      this.queryScoreRecord(row.empNo)
    },

    async queryScoreRecord(empNo) {
      try {
        this.recordLoading = true
        const params = {
          page: this.recordPageData.page,
          pageSize: this.recordPageData.pageSize,
          receiverEmpno: empNo,
          username: this.$store.state.userInfo.username
        }
        const res = await api.queryMedalList(params)
        this.scoreRecordList = res.data?.list || []
        this.scoreRecordTotal = res.data?.total || 0
      } catch (error) {
        this.$message.error('查询记录失败')
      } finally {
        this.recordLoading = false
      }
    },

    handleRecordPageChange(page) {
      this.recordPageData.page = page
      this.queryScoreRecord(this.currentUserInfo.empNo)
    },

    handleRecordPageSizeChange(pageSize) {
      this.recordPageData.pageSize = pageSize
      this.recordPageData.page = 1
      this.queryScoreRecord(this.currentUserInfo.empNo)
    },

    formatPoints(value) {
      const number = Number(value)
      return Number.isNaN(number) ? value || 0 : number
    },

    formatChangedPoints(value) {
      const number = Number(value)
      if (Number.isNaN(number)) return value || 0
      return number > 0 ? `+${number}` : number
    },

    formatRecordTime(value) {
      if (!value) return "-"
      const date = moment(value)
      return date.isValid() ? date.format("YYYY-MM-DD HH:mm:ss") : value
    },

    async handleLike(row) {
      try {
        const newStatus = row.likeStatus == 1 ? 0 : 1
        await api.giveLike({
          id: row.id,
          likeStatus: newStatus
        })
        row.likeStatus = newStatus
        this.$message.success(newStatus == 1 ? "点赞成功" : "取消点赞");
        this.queryScoreRecord(this.currentUserInfo.empNo)
      } catch (err) {
        this.$message.error("操作失败")
      }
    }
  }
}
</script>

<style lang="scss">
.ig-score-rank-page {
  box-sizing: border-box;
  min-height: calc(100vh - 120px);
  padding: 24px;
  background: #f5f7fa;

  .page-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;
    padding: 2px 4px;
  }

  .page-title {
    display: flex;
    align-items: center;
    color: #1f2937;
    font-size: 22px;
    font-weight: 700;
    line-height: 1.4;
  }

  .title-mark {
    width: 5px;
    height: 22px;
    margin-right: 10px;
    background: #1fa463;
    border-radius: 3px;
  }

  .page-subtitle {
    margin-top: 5px;
    padding-left: 15px;
    color: #909399;
    font-size: 13px;
  }

  .filter-panel {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 18px;
    padding: 18px 20px;
    background: #fff;
    border: 1px solid #ebeef5;
    border-radius: 10px;
    box-shadow: 0 2px 10px rgba(31, 41, 55, 0.04);
  }

  .filter-main {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .filter-label {
    display: flex;
    align-items: center;
    margin-right: 2px;
    color: #303133;
    font-size: 14px;
    font-weight: 600;

    i {
      margin-right: 6px;
      color: #1fa463;
      font-size: 17px;
    }
  }

  .date-range-picker {
    width: 360px;
  }

  .filter-tip {
    color: #a0a5ad;
    font-size: 12px;
  }

  .table-panel {
    padding: 20px;
    background: #fff;
    border: 1px solid #ebeef5;
    border-radius: 10px;
    box-shadow: 0 2px 10px rgba(31, 41, 55, 0.04);
  }

  .table-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
  }

  .table-title {
    color: #303133;
    font-size: 17px;
    font-weight: 600;
  }

  .total-tag {
    display: inline-block;
    margin-left: 10px;
    padding: 3px 9px;
    color: #168653;
    font-size: 12px;
    font-weight: 400;
    background: #e9f8f0;
    border-radius: 12px;
  }

  .selected-tip {
    color: #1fa463;
    font-size: 13px;
  }

  .rank-table {
    overflow: hidden;
    border-radius: 6px;
  }

  .rank-table .el-table__header th {
    height: 46px;
    color: #4b5563;
    font-weight: 600;
    background: #f5f8f6;
  }

  .rank-table .el-table__row td {
    height: 48px;
  }

  .rank-table .el-table__body tr:hover > td {
    background: #f0faf5 !important;
  }

  .points-value {
    display: inline-block;
    min-width: 48px;
    padding: 3px 9px;
    font-weight: 600;
    border-radius: 12px;
  }

  .points-value.added {
    color: #168653;
    background: #e9f8f0;
  }

  .points-value.total {
    color: #2878d0;
    background: #edf5ff;
  }

  .points-value.pending {
    color: #c47b11;
    background: #fff7e8;
  }

  .pagination {
    margin-top: 20px;
    text-align: right;
  }

  .pagination.is-background .el-pager li:not(.disabled).active {
    background: #1fa463;
  }

  @media screen and (max-width: 1100px) {
    .filter-tip {
      display: none;
    }
  }

  @media screen and (max-width: 760px) {
    padding: 14px;

    .page-header,
    .filter-panel,
    .filter-main {
      align-items: stretch;
      flex-direction: column;
    }

    .page-header,
    .filter-panel {
      gap: 14px;
    }

    .filter-main {
      gap: 10px;
    }

    .date-range-picker {
      width: 100%;
    }
  }
}

/* 弹窗挂载在 body 下，使用独立类名保证样式正常生效 */
.ig-score-record-dialog {
  overflow: hidden;
  border-radius: 10px;

  .el-dialog__header {
    padding: 20px 24px;
    border-bottom: 1px solid #ebeef5;
  }

  .el-dialog__title {
    color: #1f2937;
    font-size: 20px;
    font-weight: 600;
  }

  .el-dialog__body {
    padding: 20px 24px 24px;
  }

  .record-header {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 32px;
    margin-bottom: 20px;
    padding: 24px 28px;
    overflow: hidden;
    background: linear-gradient(135deg, #f3fbf7 0%, #ffffff 75%);
    border: 1px solid #dcefe6;
    border-radius: 12px;
    box-shadow: 0 4px 16px rgba(31, 164, 99, 0.08);
  }

  .user-profile {
    display: flex;
    /* 让用户信息区占满剩余空间，右侧积分卡保持固定不缩 */
    flex: 1;
    align-items: center;
    min-width: 360px;
  }

  .user-avatar {
    display: flex;
    flex: 0 0 60px;
    align-items: center;
    justify-content: center;
    width: 60px;
    height: 60px;
    margin-right: 18px;
    color: #fff;
    font-size: 28px;
    background: linear-gradient(135deg, #58c98d, #1fa463);
    border-radius: 50%;
    box-shadow: 0 4px 10px rgba(31, 164, 99, 0.2);
  }

  .user-content {
    min-width: 0;
  }

  .employee-name {
    margin-bottom: 10px;
    overflow: hidden;
    color: #1f2937;
    font-size: 20px;
    font-weight: 600;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .user-meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 14px;
    color: #606266;
    font-size: 18px;
  }

  .meta-item {
    display: flex;
    align-items: center;
    min-width: 0;
  }

  .meta-label {
    flex-shrink: 0;
    margin-right: 6px;
    color: #909399;
  }

  .meta-value {
    overflow: hidden;
    color: #4b5563;
    font-weight: 500;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .department-item {
    /* 部门项占满剩余空间，不再截断 */
    flex: 1;
    min-width: 0;
    max-width: none;
  }

  .meta-divider {
    width: 1px;
    height: 14px;
    background: #dcdfe6;
  }

  .score-info {
    display: flex;
    flex-shrink: 0;
    gap: 12px;
  }

  .score-card {
    box-sizing: border-box;
    width: 175px;
    padding: 15px 18px;
    border-radius: 10px;
  }

  .score-label {
    display: flex;
    align-items: center;
    margin-bottom: 8px;
    font-size: 16px;

    i {
      margin-right: 6px;
      font-size: 18px;
    }
  }

  .score-value {
    font-size: 30px;
    font-weight: 700;
    line-height: 1.2;
  }

  .total-points {
    color: #168653;
    background: #e9f8f0;
    border: 1px solid #ccecdc;
  }

  .pending-points {
    color: #c47b11;
    background: #fff7e8;
    border: 1px solid #f8e1b8;
  }

  .record-table {
    overflow: hidden;
    border-radius: 6px;
  }

  .record-table .el-table__header th {
    height: 46px;
    color: #4b5563;
    font-weight: 600;
    background: #f5f8f6;
  }

  .record-table .el-table__row td {
    height: 48px;
  }

  .record-table .el-table__body tr:hover > td {
    background: #f0faf5 !important;
  }

  .record-points {
    display: inline-block;
    min-width: 46px;
    padding: 3px 10px;
    color: #168653;
    font-weight: 600;
    background: #e9f8f0;
    border-radius: 12px;
  }

  .record-points.decrease {
    color: #d94a4a;
    background: #fff0f0;
  }

  .like-button {
    padding: 4px 8px;
    color: #909399;
    font-size: 13px;
    background: transparent;
    border: 0;
    border-radius: 4px;
    cursor: pointer;
    transition: color 0.2s, background 0.2s;

    i {
      margin-right: 4px;
      font-size: 16px;
    }

    &:hover {
      color: #1fa463;
      background: #eef9f3;
    }

    &.active {
      color: #f05252;
      background: #fff0f0;
    }
  }

  .record-pagination {
    margin-top: 18px;
    text-align: right;
  }

  .record-pagination.is-background .el-pager li:not(.disabled).active {
    background: #1fa463;
  }

  @media screen and (max-width: 900px) {
    .record-header {
      align-items: stretch;
      flex-direction: column;
    }

    .user-profile {
      min-width: 0;
    }

    .score-info {
      width: 100%;
    }

    .score-card {
      flex: 1;
      width: auto;
    }
  }
}
</style>
